import { selectIceRelayFallback, selectIceServers } from 'features/application/applicationSlice';
import { store } from 'store/store';
import { formatErrorForLogging, logger } from 'utils/logger';
import { IceLogger } from 'utils/webrtc/IceLogger';
import { applyIceRestartConfig } from 'utils/webrtc/iceRestartConfig';
import {
  IceDeathState,
  IceRecoveryLadder,
  IceRestartDecision,
} from 'utils/webrtc/IceRecoveryLadder';

export interface PrepareIceRestartArgs {
  pc: RTCPeerConnection | undefined;
  ladder: IceRecoveryLadder;
  decision: IceRestartDecision;
  handleId: string;
  feedType: string;
  reason: IceDeathState;
}

// Must run before the restart's setLocalDescription: Firefox ignores a change made after createOffer.
// The only place a live PC takes renewed servers: a changed iceServers makes its next offer a restart.
export const prepareIceRestart = ({
  pc,
  ladder,
  decision,
  handleId,
  feedType,
  reason,
}: PrepareIceRestartArgs) => {
  if (!pc) {
    return;
  }

  const state = store.getState();
  const result = applyIceRestartConfig(pc, ladder, decision, {
    relayFallback: selectIceRelayFallback(state),
    iceServers: selectIceServers(state),
  });

  if (!result.ok) {
    logger
      .remote({ system: true, capture: 'streaming' })
      .error('ICE restart: setConfiguration failed, restarting without it', {
        handleId,
        feedType,
        relayRequested: result.relayRequested,
        ...formatErrorForLogging(result.error),
      });
    return;
  }

  if (!result.relayRequested) {
    return;
  }

  IceLogger.logRelayFallbackTriggered(handleId, feedType, {
    reason,
    connectedLifetimeMs: decision.connectedLifetimeMs,
    stableReset: decision.stableReset,
    ...ladder.snapshot(),
  });
};
